import type { AccountInfo } from "@azure/msal-browser";
import { Role } from "@ask-hr/core/roles";
import { useEffect, useState } from "react";
import { Admin } from "./admin/Admin.js";
import { rolesOf, signOut } from "./auth/msal.js";
import { SiteHeader } from "./components/SiteHeader.js";
import { DEFAULT_PREFS, applyTheme, loadPrefs, resetPrefs, savePrefs, type Prefs } from "./prefs.js";
import { linkTo, usePath } from "./router.js";
import { Settings } from "./Settings.js";

// One sign-in, three views in the header like sites.novara.com: Ask HR for everyone, Admin for
// HR.Admin, and Settings. The API checks roles on every admin call; the tab is convenience.
type View = "ask" | "admin" | "settings";

function viewOf(path: string): View {
  if (path === "/admin" || path.startsWith("/admin/")) return "admin";
  if (path === "/settings") return "settings";
  return "ask";
}

export function App({ account }: { account: AccountInfo }) {
  const isAdmin = rolesOf(account).includes(Role.Admin);
  const [prefs, setPrefs] = useState<Prefs>(() => {
    const p = loadPrefs();
    // Start page: only when arriving at the root, so links and reloads keep their page.
    if (p.landing === "admin" && isAdmin && window.location.pathname === "/") {
      window.history.replaceState(null, "", "/admin");
    }
    return p;
  });
  const path = usePath();
  const view = viewOf(path);

  useEffect(() => {
    applyTheme(prefs.theme);
  }, [prefs.theme]);

  const update = (p: Partial<Prefs>) => {
    const next = { ...prefs, ...p };
    setPrefs(next);
    savePrefs(next);
  };

  const tabs: [View, string, string][] = [
    ["ask", "Ask HR", "/"],
    ...(isAdmin ? [["admin", "Admin", "/admin"] as [View, string, string]] : []),
    ["settings", "Settings", "/settings"],
  ];

  return (
    <>
      <SiteHeader title={view === "admin" ? "Ask HR Admin" : "Ask HR"}>
        {tabs.map(([id, label, to]) => (
          <a key={id} className="setbtn" href={to} onClick={linkTo(to)} aria-current={view === id ? "page" : undefined}>
            {label}
          </a>
        ))}
        <span className="who" title={account.username}>
          {account.name ?? account.username}
        </span>
        <button type="button" className="setbtn" onClick={() => void signOut()}>
          Sign out
        </button>
      </SiteHeader>
      <main className="wrap">
        {view === "admin" ? (
          isAdmin ? (
            <Admin path={path} />
          ) : (
            <section className="panel">
              <h2>Not available</h2>
              <p className="sub">Admin is for HR admins.</p>
            </section>
          )
        ) : view === "settings" ? (
          <Settings
            prefs={prefs}
            admin={isAdmin}
            update={update}
            reset={() => {
              resetPrefs();
              setPrefs(DEFAULT_PREFS);
            }}
          />
        ) : (
          <Home name={account.name ?? account.username} />
        )}
      </main>
    </>
  );
}

// Placeholder until the chat lands.
function Home({ name }: { name: string }) {
  return (
    <section className="panel">
      <h2>Hi {name.split(" ")[0]}, Ask HR is on its way</h2>
      <p className="sub">
        Answers to HR policy questions, straight from Novara's HR documents, with the page they came from.
      </p>
    </section>
  );
}
